import { authTokenLocalStorageKey } from '@/lib/api/authToken'

const REAUTH_PATH = '/userProfile/reauth'

/**
 * A session that comes back dead right after signing in again would loop:
 * one redirect per this window, after that the failure shows as an error.
 */
const REAUTH_COOLDOWN_MS = 30_000
const REAUTH_AT_KEY = 'auth.reauthAt'

export function getReauthBackTarget(locationLike: {
    pathname: string
    search?: string
    hash?: string
}) {
    return (
        locationLike.pathname +
        (locationLike.search || '') +
        (locationLike.hash || '')
    )
}

/** The sign-in screen, coming back to `back` (with any extra params). */
export function reauthPath(
    back: string,
    extra: Record<string, string> = {}
): string {
    const params = new URLSearchParams({ back, ...extra })
    return `${REAUTH_PATH}?${params.toString()}`
}

function recentlyReauthenticated(now: number): boolean {
    try {
        const at = Number(sessionStorage.getItem(REAUTH_AT_KEY))
        if (at && now - at < REAUTH_COOLDOWN_MS) return true
        sessionStorage.setItem(REAUTH_AT_KEY, String(now))
    } catch {
        // Storage blocked: no loop guard, the redirect still runs.
    }
    return false
}

/**
 * Drops the dead token and signs in again, coming back to this screen.
 * Called by the transport on every session failure; a no-op on the sign-in
 * screen itself and within the cooldown of the last redirect.
 */
export function reauthenticate(now = Date.now()) {
    if (window.location.pathname === REAUTH_PATH) return
    if (recentlyReauthenticated(now)) return
    localStorage.removeItem(authTokenLocalStorageKey)
    window.location.href = reauthPath(getReauthBackTarget(window.location))
}
